import { trpc } from '../../lib/trpc';
import type { Media, TastingExperience } from '@wineryxplorer/shared';
import { ContentContainer } from '../ContentContainer';
import { TastingExperienceCard } from '../TastingExperienceCard';

interface TastingExperiencesProps {
  venueId: string;
}

export const TastingExperiences = ({ venueId }: TastingExperiencesProps) => {
  // Query only published tasting experiences (non-admin users automatically get filtered to published only)
  const { data, isPending, error } = trpc.tastingExperience.list.useQuery(
    { venueId },
    { enabled: Boolean(venueId) }
  );

  // Cast the experiences array to proper type
  const experiences = (data?.items ?? []) as TastingExperience[];

  // Collect all hero image IDs
  const heroImageIds = experiences
    .map((exp) => exp.heroImageId)
    .filter((id): id is string => id !== undefined && id.length > 0);

  // Fetch hero images if there are any
  const { data: mediaData } = trpc.media.getByIds.useQuery(
    { ids: heroImageIds },
    { enabled: heroImageIds.length > 0 }
  );

  // Cast media array to proper type
  const mediaList = (mediaData ?? []) as Media[];

  // Create a map of media by ID for quick lookup
  const mediaMap = new Map(mediaList.map((media) => [media.id, media]));

  // Attach hero images to experiences
  const experiencesWithMedia = experiences.map((exp) => ({
    ...exp,
    heroImage: exp.heroImageId ? mediaMap.get(exp.heroImageId) : undefined,
  }));

  if (isPending) {
    return (
      <ContentContainer>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Tasting Experiences</h2>
        <p className="text-gray-600">Loading tasting experiences...</p>
      </ContentContainer>
    );
  }

  if (error) {
    return (
      <ContentContainer>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Tasting Experiences</h2>
        <p className="text-red-600">Error loading tasting experiences: {error.message}</p>
      </ContentContainer>
    );
  }

  // Don't render the section if there are no published experiences
  if (experiencesWithMedia.length === 0) {
    return null;
  }

  return (
    <ContentContainer>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Tasting Experiences</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {experiencesWithMedia.map((experience) => (
          <TastingExperienceCard key={experience.id} experience={experience} />
        ))}
      </div>
    </ContentContainer>
  );
};
